import { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { db } from "./firebase";

// Bookings use venue-local YYYY-MM-DD dates. Subtract calendar days rather than
// 48 hours so the boundary remains correct across British summer time changes.
export function recentBookingCutoff(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const value = key => parts.find(part => part.type === key).value;
  const date = new Date(`${value("year")}-${value("month")}-${value("day")}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 2);
  return date.toISOString().slice(0, 10);
}

export function useRecentBookingCutoff() {
  const [cutoff, setCutoff] = useState(recentBookingCutoff);
  useEffect(() => {
    const timer = setInterval(() => setCutoff(recentBookingCutoff()), 60000);
    return () => clearInterval(timer);
  }, []);
  return cutoff;
}

export function bookingLodgeValues(user) {
  if(user?.role!=="treasurer")return [];
  const raw=Array.isArray(user.lodgeAccess)&&user.lodgeAccess.length?user.lodgeAccess.map(l=>l?.number):[user.lodgeNumber];
  const strings=[...new Set(raw.map(n=>String(n||"").trim()).filter(Boolean))].slice(0,50);
  return [...strings,...strings.filter(n=>Number.isSafeInteger(Number(n))&&String(Number(n))===n).map(Number)];
}
export function useBookingRecords(mode, cutoff, historyCount = 50, retry = 0, user) {
  const numbers=bookingLodgeValues(user);
  const scopeKey=JSON.stringify([user?.id,user?.role,user?.active,numbers]);
  const key = `${scopeKey}:${mode}:${cutoff}:${historyCount}:${retry}`;
  const [state, setState] = useState({});
  useEffect(() => {
    if(!user?.id||user.active===false||!["treasurer","admin","superuser"].includes(user.role))return;
    const chunks=user.role==="treasurer"?Array.from({length:Math.ceil(numbers.length/30)},(_,i)=>numbers.slice(i*30,(i+1)*30)):[null];
    if(!chunks.length){setState({key,records:[],hasMore:false});return;}
    let active=true,failed=false;
    const recordsByChunk=new Map();
    const subscriptions=chunks.map((scope,index)=>{
      const bookings=collection(db,"bookings"),constraints=[];
      if(scope)constraints.push(where("lodgeNumber","in",scope));
      if(mode!=="all")constraints.push(where("date",mode==="history"?"<":">=",cutoff),orderBy("date","desc"),...(mode==="history"?[limit(historyCount+1)]:[]));
      const source=constraints.length?query(bookings,...constraints):bookings;
      return onSnapshot(source,snapshot=>{
        if(!active||failed)return;
        recordsByChunk.set(index,snapshot.docs.map(doc=>({...doc.data(),id:doc.id})));
        if(recordsByChunk.size!==chunks.length)return;
        const records=[...new Map([...recordsByChunk.values()].flat().map(row=>[row.id,row])).values()].sort((a,b)=>String(b.date).localeCompare(String(a.date)));
        setState({key,records:mode==="history"?records.slice(0,historyCount):records,hasMore:mode==="history"&&records.length>historyCount});
      },()=>{if(active){failed=true;setState({key,records:[],error:"Bookings could not be loaded. Please try again."});}});
    });
    return()=>{active=false;subscriptions.forEach(unsubscribe=>unsubscribe());};
  },[mode,cutoff,historyCount,key]);
  return state.key===key?{records:state.records,loading:false,error:state.error,hasMore:state.hasMore}:{records:[],loading:!!user?.id,error:null,hasMore:false};
}
